import { cache, type InvalidateCacheEntry, REALTIME_INVALIDATION_SOURCE } from '@customerio/hydra-nav-state';
import { useEffect } from 'react';
import { envChannel } from './channels';
import { toRealtimeEvent, type ActiveRealtime, type RealtimeEvent } from './events';
import type { Actor, EntityChange } from './message';

/** The Design Studio editor refreshes the node. */
export const DS_NODE_ENTITY_TYPE = 'ds_node';

/**
 * The embedded workflow builder reloads these. It marks every node a reload changes as an agent
 * edit, so only an agent's changes are passed on; a person's own save would otherwise come back
 * marked as the agent's.
 */
const AGENT_ONLY_ENTITY_TYPES = new Set(['campaigns', 'actions', 'notes', 'draft']);

/** Changes arriving within this long of the first one are handled together. */
export const COALESCE_MS = 250;

type PendingChange = Pick<EntityChange, 'entityType' | 'entityId' | 'parentType' | 'parentId'> & {
  actor: Actor | undefined;
};

function isRelayed(type: string, actor: Actor | undefined) {
  if (type === DS_NODE_ENTITY_TYPE) return true;
  return AGENT_ONLY_ENTITY_TYPES.has(type) && actor?.agent === true;
}

function toEntry({ entityType, entityId, parentType, parentId, actor }: PendingChange): InvalidateCacheEntry {
  const entry: InvalidateCacheEntry = { type: entityType, source: REALTIME_INVALIDATION_SOURCE };
  if (entityId !== '') entry.id = entityId;
  // The builder matches a change to the campaign it has open by its parent.
  if (parentType !== '' && parentId !== '') {
    entry.parentType = parentType;
    entry.parentId = parentId;
  }
  if (actor !== undefined) {
    entry.actor = { userId: actor.userId, source: actor.source };
    if (actor.originTab !== '') entry.actor.originTab = actor.originTab;
  }
  return entry;
}

/**
 * Collects entity changes and signals them through `cache.invalidate` in bursts. An empty entity
 * id means "every entity of the type" (the server sends it for bulk writes); the entry then leaves
 * `id` off, which is how `InvalidateCacheEntry` spells a type-wide refresh.
 */
export function createRealtimeInvalidator() {
  const pending = new Map<string, PendingChange>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  function flush() {
    timer = null;
    const entries = [...pending.values()].map(toEntry);
    pending.clear();
    if (entries.length > 0) cache.invalidate(entries);
  }

  function queue(change: PendingChange) {
    const key = [change.entityType, change.entityId, change.parentType, change.parentId].join(':');
    // One entry stands for the whole burst, so it names the origin tab only when every change in it
    // came from that tab. Otherwise that tab would skip someone else's change along with its own.
    const previous = pending.get(key);
    let { actor } = change;
    if (previous !== undefined && (previous.actor?.originTab ?? '') !== (actor?.originTab ?? '')) {
      actor = actor === undefined ? undefined : { ...actor, originTab: '' };
    }
    pending.set(key, { ...change, actor });
    timer ??= setTimeout(flush, COALESCE_MS);
  }

  return {
    handle(event: RealtimeEvent) {
      if (event.kind === 'message' && event.message.type === 'entity.changed') {
        const { data, actor } = event.message;
        if (isRelayed(data.entityType, actor)) queue({ ...data, actor });
      } else if (event.kind === 'subscribed' && event.wasRecovering && !event.recovered) {
        // The server could not replay what this tab missed while offline: refresh every node.
        queue({ entityType: DS_NODE_ENTITY_TYPE, entityId: '', parentType: '', parentId: '', actor: undefined });
      }
    },
    dispose() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      pending.clear();
    },
  };
}

/**
 * Turns server `entity.changed` messages into `cache.invalidate` signals for the embedded apps:
 * Design Studio nodes for the editor (parcel), and an agent's campaign, action, note and draft
 * changes for the workflow builder. Other entity types are ignored. Changes made by this tab come
 * back too; the editor skips the ones carrying its own `actor.originTab`.
 */
export function useRealtimeInvalidation(realtime: ActiveRealtime | null) {
  const transport = realtime?.transport;
  const environmentId = realtime?.environmentId;

  useEffect(() => {
    if (transport === undefined || environmentId === undefined) return;
    const invalidator = createRealtimeInvalidator();
    const unsubscribe = transport.subscribe(envChannel(environmentId), (raw) => {
      const event = toRealtimeEvent(raw);
      if (event !== null) invalidator.handle(event);
    });
    return () => {
      unsubscribe();
      invalidator.dispose();
    };
  }, [transport, environmentId]);
}
